import { computed, defineComponent, shallowRef } from 'vue';
// import useConfigInject from '../config-provider/hooks/useConfigInject';
import useConfigInject from '../_util/hooks/useConfigInject';
import Popover from '../popover';
import { generateColor } from './util';
import PropTypes from '../_util/vue-types';
import useMergedState from '../_util/hooks/useMergedState';
import classNames from '../_util/classNames';
import ColorPickerPanel from './ColorPickerPanel';
import ColorTrigger from './components/ColorTrigger';
import useColorState from './hooks/useColorState';
const DefaultColor = '#ffffff';
const colorPickerProps = () => ({
    value: {
        type: [String, Object],
        default: undefined,
    },
    defaultValue: {
        type: [String, Object],
        default: DefaultColor,
    },
    open: PropTypes.looseBool,
    disabled: PropTypes.looseBool,
    placement: {
        type: String,
        default: 'bottomLeft',
    },
    trigger: {
        type: String,
        default: 'click',
    },
    format: {
        type: String,
        default: 'hex',
    },
    formatOptions: {
        type: Array,
        default: undefined
    },
    allowClear: {
        type: Boolean,
        default: false,
    },
    presets: {
        type: Array,
        default: undefined,
    },
    arrow: {
        type: [Boolean, Object],
        default: true,
    },
    styles: {
        type: Object,
        default: () => ({}),
    },
    rootClassName: PropTypes.string,
    onOpenChange: {
        type: Function,
        default: () => { },
    },
    onFormatChange: {
        type: Function,
        default: () => { },
    },
    onChange: {
        type: Function,
        default: () => { },
    },
    getPopupContainer: {
        type: Function,
        default: undefined,
    },
    autoAdjustOverflow: {
        type: Boolean,
        default: true,
    },
    onChangeComplete: {
        type: Function,
        default: () => { },
    },
    showText: {
        type: [Boolean, Function],
        default: false,
    },
    size: PropTypes.oneOf(['small', 'middle', 'large']),
    destroyTooltipOnHide: PropTypes.looseBool,
    panelRender: {
        type: Function,
        default: undefined,
    },
});
const ColorPicker = defineComponent({
    name: 'PfColorPicker',
    inheritAttrs: false,
    props: colorPickerProps(),
    setup(props, { slots, attrs, emit }) {
        const { prefixCls, getPopupContainer, direction, size } = useConfigInject('color-picker', props);
        const value = computed(() => props.value);
        const [colorValue, setColorValue] = useColorState(DefaultColor, {
            value,
            defaultValue: props.defaultValue,
        });
        const open = computed(() => props.open);
        const [popupOpen, setPopupOpen] = useMergedState(false, {
            value: open,
            postState: openData => !props.disabled && openData,
            onChange: props.onOpenChange,
        });
        const format = computed(() => props.format);
        const [formatValue, setFormatValue] = useMergedState(props.format, {
            value: format,
            onChange: props.onFormatChange,
        });
        // ===================== Style =====================
        const mergedSize = computed(() => props.size || size.value);
        const rtlCls = computed(() => ({ [`${prefixCls.value}-rtl`]: direction.value }));
        const mergeCls = computed(() => {
            const mergeRootCls = classNames(props.rootClassName, rtlCls.value);
            return classNames({
                [`${prefixCls.value}-sm`]: mergedSize.value === 'small',
                [`${prefixCls.value}-lg`]: mergedSize.value === 'large',
            }, mergeRootCls);
        });
        const mergePopupCls = computed(() => classNames(prefixCls.value, rtlCls.value));
        const colorCleared = shallowRef(false);
        const popupAllowCloseRef = shallowRef(true);
        const handleChange = (data, type, pickColor) => {
            let color = generateColor(data);
            const isNull = value.value === null || (!value.value && props.defaultValue === null);
            if (colorCleared.value || isNull) {
                colorCleared.value = false;
                const hsba = color.toHsb();
                // ignore alpha slider
                if (colorValue.value.toHsb().a === 0 && type !== 'alpha') {
                    hsba.a = 1;
                    color = generateColor(hsba);
                }
            }
            if (pickColor) {
                popupAllowCloseRef.value = false;
            }
            setColorValue(color);
            emit('update:value', color, color.toHexString());
            emit('change', color, color.toHexString());
        };
        const handleClear = () => {
            colorCleared.value = true;
            emit('clear');
        };
        const handleChangeComplete = color => {
            popupAllowCloseRef.value = true;
            emit('changeComplete', generateColor(color));
        };
        const onFormatChange = (format) => {
            setFormatValue(format);
            emit('formatChange', format);
        };
        const popoverProps = computed(() => ({
            visible: popupOpen.value,
            trigger: props.trigger,
            placement: props.placement,
            arrow: props.arrow,
            rootClassName: props.rootClassName,
            getPopupContainer: getPopupContainer.value,
            autoAdjustOverflow: props.autoAdjustOverflow,
            destroyTooltipOnHide: props.destroyTooltipOnHide,
        }));
        const colorBaseProps = computed(() => ({
            prefixCls: prefixCls.value,
            color: colorValue.value,
            allowClear: props.allowClear,
            colorCleared: colorCleared.value,
            disabled: props.disabled,
            presets: props.presets,
            format: formatValue.value,
            formatOptions: props.formatOptions,
            panelRender: props.panelRender,
            onFormatChange,
            onChangeComplete: handleChangeComplete,
        }));
        return () => {
            return (<Popover 
            // @ts-ignore
            style={props.styles?.popup} 
            // @ts-ignore
            overlayInnerStyle={props.styles?.popupOverlayInner} onVisibleChange={visible => {
                    if (popupAllowCloseRef.value) {
                        setPopupOpen(visible);
                    }
                }} content={<ColorPickerPanel {...colorBaseProps.value} onChange={handleChange} onClear={handleClear}/>} overlayClassName={mergePopupCls.value} {...popoverProps.value}>
          {slots.children?.() || (<ColorTrigger open={popupOpen.value} class={mergeCls.value} style={attrs.style} color={props.value ? generateColor(props.value) : colorValue.value} prefixCls={prefixCls.value} disabled={props.disabled} showText={props.showText} colorCleared={colorCleared.value}/>)}
        </Popover>);
        };
    },
});
export default ColorPicker;
